import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FaArrowRight, FaCheck } from "react-icons/fa";

/**
 * Hero pitch: "I build ___", cycling through real products. For each one the four stages of a
 * build — data, API, interface, ship — light up in turn, then the shipped case study is revealed.
 */
const BUILDS = [
  { phrase: "workflow engines", project: "BPMFlow", to: "/projects/bpmflow" },
  { phrase: "real-estate platforms", project: "ImmoFlow", to: "/projects/immoflow" },
  { phrase: "white-label CRMs", project: "CRMBuzz", to: "/projects/crmbuzz" },
  { phrase: "job application tools", project: "Aplivia", to: "/projects/aplivia" },
  { phrase: "developer communities", project: "CodeQuery", to: "/projects/codequery" },
];
const LAYERS = ["Data", "API", "Interface", "Ship"];

const STEP_MS = 520; // one layer lights up
const HOLD_MS = 2200; // pause on the finished build before the next one

const HeroPitch = () => {
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [lit, setLit] = useState(0); // how many layers are lit
  const [paused, setPaused] = useState(false);

  const build = BUILDS[index];
  const shipped = lit >= LAYERS.length;

  useEffect(() => {
    if (reducedMotion) {
      setLit(LAYERS.length);
      return undefined;
    }
    if (paused) return undefined;
    const timer = setTimeout(
      () => {
        if (shipped) {
          setLit(0);
          setIndex((i) => (i + 1) % BUILDS.length);
        } else {
          setLit((n) => n + 1);
        }
      },
      shipped ? HOLD_MS : STEP_MS
    );
    return () => clearTimeout(timer);
  }, [lit, shipped, paused, reducedMotion]);

  return (
    <div
      className="hero-pitch"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="hero-pitch__eyebrow">Hey there 👋 Full-stack engineer, AI included</span>

      {/* Screen readers get one stable sentence instead of the rotating words. */}
      <span className="hero-pitch__sr">
        I build complete web platforms end to end: workflow engines, real-estate platforms, white-label
        CRMs, job application tools and developer communities.
      </span>
      <div className="hero-pitch__headline" aria-hidden="true">
        <span className="hero-pitch__lead">I build</span>
        <span className="hero-pitch__slot">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={build.phrase}
              className="hero-pitch__phrase"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {build.phrase}
            </motion.span>
          </AnimatePresence>
        </span>
      </div>

      <ol className="hero-pitch__layers" aria-label="What I build, end to end">
        {LAYERS.map((layer, i) => (
          <li key={layer} className={`hero-pitch__layer${i < lit ? " is-lit" : ""}`}>
            <span className="hero-pitch__node">{i < lit && <FaCheck aria-hidden="true" />}</span>
            <span className="hero-pitch__label">{layer}</span>
          </li>
        ))}
      </ol>

      <div className="hero-pitch__footer">
        <Link
          to={build.to}
          className={`hero-pitch__shipped${shipped ? " is-visible" : ""}`}
          tabIndex={shipped ? 0 : -1}
        >
          Shipped as <strong>{build.project}</strong> <FaArrowRight size={10} aria-hidden="true" />
        </Link>
        <a href="#about" className="hero-pitch__more">Read more about me</a>
      </div>
    </div>
  );
};

export default HeroPitch;
